import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'src/utils/axios';
import { setElevatedAuth } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';

const REFRESH_DEBOUNCE_MS = 30_000; // fire at most once per 30 s
const REFRESH_WITHIN_MS = 5 * 60 * 1000; // only when within 5 min of expiry

/**
 * Listens for user activity (click / keypress) on the document.
 * When the elevated-auth window is valid AND within 5 minutes of expiry,
 * fires POST /api/v2/user/elevated-auth/refresh/ — debounced to at most once per 30s.
 * Uses document.addEventListener (NOT window.addEventListener — Loki rule).
 */
function useElevatedAuthRefresh(
  isValid: boolean,
  expiresAt: string | null
): void {
  const dispatch = useDispatch();
  const lastRefreshRef = useRef<number>(0);

  useEffect(() => {
    if (!isValid) {
      return;
    }

    const handleActivity = (): void => {
      const now = Date.now();
      if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) {
        return;
      }
      if (!expiresAt) {
        return;
      }
      const timeUntilExpiry = new Date(expiresAt).getTime() - now;
      if (timeUntilExpiry > REFRESH_WITHIN_MS) {
        return;
      }
      lastRefreshRef.current = now;

      const refreshElevatedAuth = async (): Promise<void> => {
        try {
          const resp = await axios.post('v2/user/elevated-auth/refresh/');
          dispatch(
            setElevatedAuth({
              verified: true,
              expires_at: resp.data.expires_at,
            })
          );
        } catch {
          // Silently ignore — expiry watcher will handle if session truly expired
        }
      };

      void refreshElevatedAuth();
    };

    document.addEventListener('click', handleActivity);
    document.addEventListener('keypress', handleActivity);
    return () => {
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('keypress', handleActivity);
    };
  }, [isValid, expiresAt, dispatch]);
}

export default useElevatedAuthRefresh;
